import { Link } from '@tanstack/react-router'
import { RowActions } from '@trellis/roots/data'
import type { RowAction } from '@trellis/roots/data'
import type { ExpenseResponse } from '@trellis/shared'
import { CheckCircle2, Copy, Edit, FileText, Trash2, XCircle } from 'lucide-react'

export function ExpenseRowActionsMenu({
  expense,
  onDeleteClick,
  onPreviewClick,
  onDuplicateClick,
  onBillClick,
  onClaimClick,
  onUnclaimClick,
}: {
  expense: ExpenseResponse
  onDeleteClick: (expense: ExpenseResponse) => void
  onPreviewClick: (expense: ExpenseResponse) => void
  onDuplicateClick: (expense: ExpenseResponse) => void
  onBillClick?: (expense: ExpenseResponse) => void
  onClaimClick?: (expense: ExpenseResponse) => void
  onUnclaimClick?: (expense: ExpenseResponse) => void
}) {
  const isBilled = expense.billed === true
  const isOnDraftInvoice = !isBilled && expense.invoiceLineItemId !== null

  const actions: RowAction[] = [
    {
      label: 'Edit',
      icon: <Edit className="mr-2 h-4 w-4" />,
      href: `/expenses/${expense.id}/edit`,
    } satisfies RowAction,
    {
      label: 'Duplicate',
      icon: <Copy className="mr-2 h-4 w-4" />,
      onClick: () => onDuplicateClick(expense),
    } satisfies RowAction,
    ...(expense.type === 'BUSINESS' && !expense.claimed && onClaimClick
      ? [
          {
            label: 'Mark as Claimed',
            icon: <CheckCircle2 className="mr-2 h-4 w-4" />,
            onClick: () => onClaimClick(expense),
          } satisfies RowAction,
        ]
      : []),
    ...(expense.type === 'BUSINESS' && expense.claimed && onUnclaimClick
      ? [
          {
            label: 'Unmark as Claimed',
            icon: <XCircle className="mr-2 h-4 w-4" />,
            onClick: () => onUnclaimClick(expense),
          } satisfies RowAction,
        ]
      : []),
    ...(expense.type === 'REIMBURSEMENT' && !isBilled && !isOnDraftInvoice && onBillClick
      ? [
          {
            label: 'Add to invoice',
            icon: <FileText className="mr-2 h-4 w-4" />,
            onClick: () => onBillClick(expense),
          } satisfies RowAction,
        ]
      : []),
    ...(!isBilled
      ? [
          {
            label: 'Delete',
            icon: <Trash2 className="mr-2 h-4 w-4" />,
            onClick: () => onDeleteClick(expense),
            destructive: true,
            separator: true,
          } satisfies RowAction,
        ]
      : []),
  ]

  return (
    <RowActions
      onViewClick={() => onPreviewClick(expense)}
      triggerLabel="Open expense actions"
      actions={actions}
      linkComponent={Link}
    />
  )
}
